import React, { useCallback, useEffect, useState, useMemo } from 'react'
import { Box, Grid, Stack, TextField, Typography, InputAdornment, IconButton, Card } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
// icons
import SearchIcon from 'common/customIcons/search';
import DeleteIcon from "common/customIcons/delete";
import AngleRightIcon from "common/customIcons/angle_right";
// utils and helpers
import { debounce, numberOnly } from "utilities/common";
import { validateKitProducts } from "validations/hyperwolf/distributionmanagement";
import { deleteTemplateKitProducts, getAssignedProducts, getTemplateKitDetails, updateAssignedProducts } from "../../../../redux/slices/hyperwolf/inventoryDistribution";
import { closeCustomModalDialog, openCustomModalDialog, setModalLoader } from "../../../../redux/slices/modalSlice";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from "components/MDButton";
import { BrandFilter, SubCategoryFilter, BoxFilter } from "common/filters";
import InfiniteScrollTable from "components/tables/infiniteScrollTable";

export const QuantityCellRenderer = ({ props, setSelectedProducts, selectedBoxes, selectedBulkQuantity, errors = [], disallowZero = false }) => {
  const { data = {}, colDef = {} } = props || {};
  const [inputValue, setInputValue] = useState(data?.[colDef.field] || "")

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formattedValue = numberOnly(value) ?? "";

    if (disallowZero && /^0+$/.test(formattedValue)) return;

    setInputValue(formattedValue);
    setSelectedProducts(prev => {
      const newProducts = [...prev];
      const categoryIndex = newProducts.findIndex(item => item.boxId === data.boxId);

      if (categoryIndex >= 0) {
        const productIndex = newProducts[categoryIndex]?.products?.findIndex(item => item?.productId === data.productId);
        if (productIndex >= 0) {
          newProducts[categoryIndex].products[productIndex][name] = formattedValue;
        } else {
          newProducts[categoryIndex].products.push({ productId: data.productId, [name]: formattedValue });
        }
      } else {
        newProducts.push({
          boxId: data.boxId,
          products: [{ productId: data?.productId, [name]: formattedValue }]
        })
      }
      return newProducts;
    })
  };

  useEffect(() => {
    const boxQuantities = selectedBoxes?.find(box => box?.boxId === data?.boxId) || {};
    setInputValue(boxQuantities?.[colDef.field] || selectedBulkQuantity?.[colDef.field] || "");
  }, [selectedBoxes, selectedBulkQuantity]);

  const hasError = useMemo(() => {
    return Array.isArray(errors) && errors?.find(error => error?.productId === data?.productId && error?.maxQuantity);
  }, [errors])

  return (
    <div className="table-input-box">
      <TextField
        className="form-input"
        placeholder="Value"
        defaultValue={data?.[colDef.field] || ""}
        name={colDef.field}
        value={inputValue}
        onChange={handleChange}
      />
      {hasError && colDef.field === "maxQuantity" && <p className="error">{hasError?.maxQuantity}</p>}
    </div>
  );
};

const AssignProductsToTemplateKit = () => {
  const { templateId } = useParams()
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams()
  const { updating } = Object.fromEntries(searchParams.entries())

  const { isOpen } = useSelector(state => state.modal);

  const [errors, setErrors] = useState([])
  const [details, setDetails] = useState({})
  const [inputFilters, setInputFilters] = useState({});
  const [customFilters, setCustomFilters] = useState({});
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedBoxes, setSelectedBoxes] = useState([]);
  const [selectedBulkQuantity, setSelectedBulkQuantity] = useState({});
  const [refreshTable, setRefreshTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const columnDefs = useMemo(() => {
    return [
      {
        headerName: 'Product Name/SKU',
        field: 'adminProductName',
        suppressMenu: false,
        flex: 2,
        sortable: false,
        cellRenderer: (params) => {
          if (!params?.data) return "-";
          const { adminProductName, sku, assets } = params?.data || {};
          const { thumbURL, name } = assets?.[0] || {}

          return <Box style={{ display: "flex", gap: "10px" }}>
            <img src={thumbURL} alt={name} width={40} height={40} />
            <Box>
              <Typography variant='body2' style={{ color: "#2E2E3A", fontWeight: "500", fontSize: "12px" }}>{adminProductName}</Typography>
              {sku && <Typography variant='body2' style={{ color: "#9A9AAF", fontSize: "12px" }}>{sku}</Typography>}
            </Box>
          </Box>
        }
      },
      {
        headerName: 'Category',
        field: 'category',
        suppressMenu: false,
        flex: 1.5,
        sortable: false,
        valueGetter: ({ data }) => data?.category?.name || "-",
      },
      {
        headerName: 'Brand',
        field: 'brand',
        suppressMenu: false,
        flex: 1.25,
        sortable: false,
        valueGetter: ({ data }) => data?.brand?.name || "-",
      },
      {
        headerName: 'Price per piece',
        field: 'pricePerPiece',
        suppressMenu: false,
        sortable: false,
        flex: 0.75,
        valueGetter: ({ data }) => {
          return data?.pricePerPiece ? `$${data?.pricePerPiece}` : "$0"
        }
      },
      {
        headerName: 'Min Quantity',
        field: 'minQuantity',
        suppressMenu: false,
        flex: 1,
        sortable: false,
        suppressReactCellRendererRefresh: true,
        cellRenderer: (props) => <QuantityCellRenderer
          props={props}
          setSelectedProducts={setSelectedProducts}
          selectedBoxes={selectedBoxes}
          selectedBulkQuantity={selectedBulkQuantity}
          errors={errors}
        />
      },
      {
        headerName: 'Max Quantity',
        field: 'maxQuantity',
        suppressMenu: false,
        flex: 0.75,
        sortable: false,
        suppressReactCellRendererRefresh: true,
        cellRenderer: (props) => <QuantityCellRenderer
          props={props}
          setSelectedProducts={setSelectedProducts}
          selectedBoxes={selectedBoxes}
          selectedBulkQuantity={selectedBulkQuantity}
          errors={errors}
        />
      },
      {
        headerName: 'Action',
        field: 'actions',
        suppressMenu: false,
        flex: 0.75,
        sortable: false,
        cellRenderer: (params) => {
          if (!params?.data) return "-";

          return <Box display="flex" flexDirection="row" gap={0.5} className="field-actions">
            <IconButton
              color='error'
              size='small'
              variant='primary'
              onClick={(e) => {
                e.stopPropagation();
                dispatch(openCustomModalDialog({
                  modalData: { deleteHandler: () => handleDeleteProduct(params?.data, "product") },
                  modal: 'DELETE_MODAL'
                }))
              }}
            >
              <DeleteIcon width='16px' height='16px' />
            </IconButton>
          </Box>
        },
      },
    ]
  }, [selectedProducts, errors])

  const handleTemplateKitDetails = async () => {
    try {
      const response = await dispatch(getTemplateKitDetails({ templateId })).unwrap();
      if (response?.data) {
        setDetails(response?.data);

        const boxes = response?.data?.boxes || [];
        const productsData = boxes.map(box => ({ boxId: box.boxId, products: [] }));
        setSelectedProducts(productsData);
      }
    } catch (error) {
      showToastMsg(error);
    }
  }

  const handleChange = async (e) => {
    const { name, value } = e.target

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }

  const handleDebounce = useCallback(debounce((event) => {
    const { name, value } = event.target;

    setCustomFilters(prev => ({ ...prev, [name]: value }));
    setRefreshTable(prev => !prev)
  }, 1000), []);

  const handleClearFilters = () => {
    setCustomFilters({ search: customFilters.search || "" })
    setRefreshTable(prev => !prev)
  }

  const handleDeleteProduct = async (data = {}, type) => {
    try {
      const request = {
        templateId: templateId,
        boxId: data?.boxId,
        productId: type === "category" ? "" : data.productId
      }

      dispatch(setModalLoader(true));
      const response = await dispatch(deleteTemplateKitProducts(request)).unwrap();
      if (response?.message) {
        showToastMsg(response?.message || "Success", "success");
        if (type === "category") {
          handleTemplateKitDetails();
        } else {
          setRefreshTable(prev => !prev)
        }
        dispatch(closeCustomModalDialog());
      }
    }
    catch (error) {
      showToastMsg(error)
    } finally {
      dispatch(setModalLoader(false));
    }
  }

  const handleDeleteBox = async (data = {}) => {
    try {
      const request = {
        templateId: templateId,
        boxId: data?.boxId,
        productId: null
      }

      dispatch(setModalLoader(true));
      const response = await dispatch(deleteTemplateKitProducts(request)).unwrap();
      if (response?.message) {
        showToastMsg(response?.message || "Success", "success");
        dispatch(closeCustomModalDialog());
        if (setRefreshTable) {
          setRefreshTable(prev => !prev)
        }
      }
    }
    catch (error) {
      showToastMsg(error)
    } finally {
      dispatch(setModalLoader(false));
    }
  }

  const handleSave = async () => {
    if (!details?.boxes?.length) return setErrors(prev => ({ ...prev, boxes: "Atleast one box is required" }));

    const { myErrors, isValid } = validateKitProducts(selectedProducts);
    if (!isValid) {
      setErrors(myErrors);
      return;
    } else {
      setErrors({});
    }

    if (updating === "true") {
      dispatch(openCustomModalDialog({ modalData: { templateId, selectedProducts, selectedBoxes, selectedBulkQuantity }, modal: 'PLANNER_OVERRIDE_NOTES' }))
      return;
    }

    if (isLoading) return;
    setIsLoading(true);

    try {
      const request = {
        templateId,
        plannerOverrideNotes: {},
        products: selectedProducts,
        boxes: selectedBoxes || [],
        ...selectedBulkQuantity
      };

      const response = await dispatch(updateAssignedProducts(request)).unwrap();
      if (response?.data) {
        navigate(`/hyperwolf/kit-template/view-template/${templateId}`);
      }
    } catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    if (!isOpen) {
      handleTemplateKitDetails();
    }
  }, [isOpen])

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }} className="ag-grid-table-white">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1} onClick={() => navigate(-1)}>
            <IconButton>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="h6" fontWeight="semibold" ml={0.75}>
              Template
            </Typography>
          </Box>
        </Stack>

        <Card sx={{ p: { xs: 2, md: 3 }, mb: 2, borderRadius: 2.5, boxShadow: "none" }}>

          {/* Top Section */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ sm: "center" }}
                justifyContent="space-between"
                gap={2}
                my={2}
              >
                <Typography variant='h5' color="#656575" fontWeight="semibold">{"Add SKU's To Template"}</Typography>

                {/* Search */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ sm: "center" }}
                  justifyContent="space-between"
                  gap={2}
                  className="search-gray"
                >
                  <TextField
                    className="custom-search-field"
                    placeholder="Search"
                    label=""
                    InputProps={{
                      startAdornment: <InputAdornment position="start">
                        <IconButton size='small'>
                          <SearchIcon width='18px' height='18px' />
                        </IconButton>
                      </InputAdornment>
                    }}
                    name="search"
                    value={inputFilters.search}
                    onChange={event => {
                      const { value } = event.target;
                      setInputFilters(prev => ({ ...prev, search: value }))
                      handleDebounce(event)
                    }}
                    style={{ minWidth: "300px" }}
                  />

                  {/* Add button */}
                  <MDButton
                    sx={{ minWidth: "160px", background: "#2E2E3A !important", color: "#ffffff !important", boxShadow: "none !important" }}
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={() => dispatch(openCustomModalDialog({ modalData: { templateId, setRefreshTable }, modal: 'ASSIGN_PRODUCTS' }))}
                  >
                    + Add SKU
                  </MDButton>

                  {/* Assign qty button */}
                  <MDButton
                    style={{ fontWeight: "600", boxShadow: "none", background: "#0163FF", color: "#ffffff" }}
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      dispatch(openCustomModalDialog({ modalData: { setSelectedBulkQuantity }, modal: 'BULK_ASSIGN_QTY' }))
                    }}
                  >
                    Bulk Assign Qty
                  </MDButton>
                </Stack>
              </Stack>
            </Grid>
          </Grid>
          <Grid item xs={12}>

            {/* Filters */}
            <Box className='cus-filter-sec filter-gray-bg' mb={2}>
              <Grid className='filter-box' container alignItems='center' justifyContent='flex-start' spacing={1.5}>
                <Grid item xs={12} lg={3} xxxl={2}>
                  <BoxFilter
                    value={customFilters?.boxId}
                    handleChange={handleChange}
                    placeholder={"Box Type"}
                    selectAllLabel={"All Boxes"}
                  />
                </Grid>
                <Grid item xs={12} lg={3} xxxl={2}>
                  <SubCategoryFilter
                    value={customFilters?.categoryId}
                    handleChange={handleChange}
                    placeholder={"Category"}
                    selectAllLabel={"All Categories"}
                  />
                </Grid>
                <Grid item xs={12} lg={3} xxxl={2}>
                  <BrandFilter
                    value={customFilters?.brandId}
                    handleChange={handleChange}
                    placeholder={"Brands"}
                    selectAllLabel={"All"}
                  />
                </Grid>
                <Grid item xs={12} lg={3} xxxl={2}>
                  <MDButton
                    sx={{ minWidth: "120px", fontWeight: "normal" }}
                    type="button"
                    variant="outlined"
                    color="dark"
                    disabled={!Object.values(customFilters || {})?.filter(Boolean)?.length}
                    onClick={handleClearFilters}
                  >
                    Clear All
                  </MDButton>
                </Grid>
              </Grid>
            </Box>

            <Box className="cus-acc-white">
              {/* Products listing */}
              <InfiniteScrollTable
                tableName="Box Products"
                getDataMethod={getAssignedProducts}
                columnDefs={columnDefs}
                customFilters={{ ...customFilters, templateId }}
                refreshTable={refreshTable}
                setRefreshTable={setRefreshTable}
                showDeleteBoxButton={true}
                handleDeleteBox={handleDeleteBox}
                setSelectedBoxes={setSelectedBoxes}
                tableHeight="65vh"
              />
            </Box>

            {errors?.boxes && <p className="error">{errors?.boxes}</p>}

            {/* Action buttons */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={4} mb={2}>
              <MDButton
                sx={{ minWidth: "160px" }}
                type="button"
                variant="contained"
                color="primary"
                disabled={!details?.boxes?.length}
                onClick={handleSave}
              >
                {'Complete & Save'}
              </MDButton>
              <MDButton
                sx={{ minWidth: "160px" }}
                type="button"
                variant="outlined"
                color="dark"
                onClick={() => navigate(-1)}
              >
                Cancel
              </MDButton>
            </Stack>

          </Grid>
        </Card>
      </Box>
    </CommonLayout>
  )
}

export default AssignProductsToTemplateKit
